import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { validateInviteCode, sanitizeInput } from "@/shared/utils";
import type { OTPFormData } from "@/features/auth/types";

interface OTPFormProps {
  onOTPSubmit: (otpCode: string) => void;
  onBack: () => void;
  isLoading?: boolean;
  error?: string;
  userName?: string;
}

export function OTPForm({ onOTPSubmit, onBack, isLoading = false, error, userName }: OTPFormProps) {
  const [formData, setFormData] = useState<OTPFormData>({
    otp: "",
  });
  const [fieldError, setFieldError] = useState<string>("");

  const validateForm = (): boolean => {
    if (!formData.otp.trim()) {
      setFieldError("Please enter the verification code");
      return false;
    }

    if (!validateInviteCode(formData.otp)) {
      setFieldError("Please enter a valid 6-digit verification code");
      return false;
    }

    setFieldError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onOTPSubmit(sanitizeInput(formData.otp));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = sanitizeInput(e.target.value);
    setFormData({ otp: value });

    // Clear field error when user starts typing
    if (fieldError) {
      setFieldError("");
    }

    // Clear general error when user starts typing
    if (error) {
      // Clear error by calling onBack with a no-op or similar
      // This is a limitation of the current API design
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Two-Factor Authentication</CardTitle>
          <CardDescription className="text-center">
            Enter the verification code sent to your registered device
            {userName && (
              <span className="block mt-2 font-medium text-foreground">
                Welcome, {userName}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">Verification Code</Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={formData.otp}
                onChange={handleInputChange}
                disabled={isLoading}
                required
                maxLength={6}
                pattern="[A-Z0-9]{6}"
                aria-invalid={!!fieldError}
                className="text-center text-lg tracking-widest"
              />
              {fieldError && (
                <p className="text-sm text-destructive">{fieldError}</p>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Verifying..." : "Verify Code"}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={onBack}
                disabled={isLoading}
              >
                Back to Login
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>If you didn't receive a code, please check your device or contact support.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}